import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronsDown } from "lucide-react";
import { Bild } from "./Bild";
import { START, BILDER } from "../lib/inhalte";

// Vorher → Nachher beim Scrollen: der neue Boden wischt über den alten (gleicher Blickwinkel).
// Nur wenn beide Bilder zusammenpassen (beide vorhanden oder beide Platzhalter); sonst ruhiges Titelbild.
const WISCHEN = !!BILDER.hero_vorher.datei === !!BILDER.hero_nachher.datei;

export const Hero = () => {
  const h = START.hero;
  const bereich = useRef(null);
  const [p, setP] = useState(0);

  useEffect(() => {
    if (!WISCHEN) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setP(0.5);
      return undefined;
    }
    let rahmen = 0;
    const messen = () => {
      rahmen = 0;
      const r = bereich.current.getBoundingClientRect();
      const weg = r.height - window.innerHeight;
      setP(Math.min(1, Math.max(0, -r.top / (weg * 0.8))));
    };
    const onScroll = () => {
      if (!rahmen) rahmen = requestAnimationFrame(messen);
    };
    messen();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  if (!WISCHEN) {
    return (
      <section data-testid="hero-section" className="relative h-[92vh] min-h-[560px] overflow-hidden">
        <Bild id="hero_nachher" className="absolute inset-0 w-full h-full" />
        <Titelkarte h={h} />
      </section>
    );
  }
  return (
    <section ref={bereich} data-testid="hero-section" className="relative h-[230vh]">
      <div className="sticky top-0 h-screen overflow-hidden">
        <Bild id="hero_vorher" platzhalter="alt" className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - p * 100}% 0 0)` }}>
          <Bild id="hero_nachher" platzhalter="neu" className="absolute inset-0 w-full h-full" />
        </div>
        <div
          aria-hidden="true"
          className="absolute top-0 bottom-0 w-[3px] bg-white shadow-[0_0_20px_rgba(0,0,0,0.35)]"
          style={{ left: `${p * 100}%`, opacity: p > 0.01 && p < 0.99 ? 1 : 0 }}
        />
        <div className="absolute top-20 left-4 sm:left-6 flex gap-2 text-xs font-semibold">
          <span className={`px-2.5 py-1 rounded-full transition-colors ${p < 0.5 ? "bg-white text-stein-900" : "bg-black/30 text-white/80"}`}>
            {h.vorher}
          </span>
          <span className={`px-2.5 py-1 rounded-full transition-colors ${p >= 0.5 ? "bg-chemara-violet text-white" : "bg-black/30 text-white/80"}`}>
            {h.nachher}
          </span>
        </div>

        <Titelkarte h={h}>
          <div className={`mt-4 flex items-center gap-2 text-xs text-stein-500 transition-opacity ${p > 0.05 ? "opacity-0" : "opacity-100"}`}>
            <ChevronsDown size={14} className="animate-bounce" /> {h.scroll}
          </div>
        </Titelkarte>
      </div>
    </section>
  );
};

const Titelkarte = ({ h, children }) => (
  <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 md:p-10">
    <div className="max-w-6xl mx-auto">
      <div className="max-w-xl bg-stein-50/95 backdrop-blur rounded-xl p-6 md:p-8 shadow-xl">
        <h1 data-testid="hero-headline" className="font-heading text-stein-900 text-[27px] min-[400px]:text-[31px] sm:text-5xl leading-[1.05] break-words">
          {h.titel}
          <span className="block text-xl sm:text-2xl mt-2 text-stein-700">{h.titel_zusatz}</span>
        </h1>
        <p className="mt-4 text-[15px] sm:text-base text-stein-700 leading-relaxed">{h.text}</p>
        <Link
          to="/offerte"
          data-testid="hero-cta"
          className="mt-6 flex sm:inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-chemara-violet text-white font-semibold hover:bg-chemara-violet-deep transition-colors"
        >
          {h.cta} <ArrowRight size={18} />
        </Link>
        {children}
      </div>
    </div>
  </div>
);
